import { RequestStatus } from 'constants/RequestStatus';

import { PayloadAction } from '@reduxjs/toolkit';
import {
  BulkCreationPayload,
  LoadScenariosPayload,
  ScenarioByIdRequestPayload,
  ScenarioCreateRequestPayload,
  ScenarioRenameRequestPayload,
  ScenarioSavingResponse,
  ScenarioSharingRequestPayload,
  ScenarioUpdateRequestPayload,
} from 'api/scenarios';

import {
  buildMapWellsSettings,
  getScenarioKey,
  resetScenarioStateToInitPoint,
} from 'app/components/containers/layout/ConfigTabs/Scenario/utils';
import { cloneThroughJson } from 'helpers/objects';
import { ScenariosPermission } from 'types/Scenario';
import { createSlice } from 'utils/@reduxjs/toolkit';

import {
  getDefaultScenarioSettingsToLoadOnMap,
  getScenarioSavedDataDefaultState,
  getUnloadableScenarioSettingsToLoadOnMap,
} from './config';
import { DEFAULT_SCENARIO_NAME } from './constants';
import {
  EScenarioType,
  IEconomicsSavingData,
  IPlanSavingData,
  IScenarioTypeScenariosPage,
  IScenariosSharedProperties,
  Scenario,
  ScenarioDetails,
  ScenarioSharingObjectError,
  ScenariosState,
} from './types';

export const scenarioSharedInitialState: IScenariosSharedProperties = {
  firstRunSelectedScenario: true,
  scenariosLoadingStatus: RequestStatus.IDLE,
  sharingStatus: RequestStatus.IDLE,
  scenarioLoadingStatus: RequestStatus.IDLE,
  creatingStatus: RequestStatus.IDLE,
  creatingError: null,
  updatingStatus: RequestStatus.IDLE,
  updatingError: null,
  copyingStatus: RequestStatus.IDLE,
  copiedScenarioId: null,
  copyingError: null,
  leavingStatus: RequestStatus.IDLE,
  leavingError: null,
  deletingStatus: RequestStatus.IDLE,
  deletingError: null,
  renamingStatus: RequestStatus.IDLE,
  renamingError: null,
  scenarioListLoadingError: null,
  sharingError: null,
  scenarioLoadingError: null,
  scenarios: [],
  scenarioSavingFormActive: false,
  scenarioRenamingInPanel: false,
  scenarioRenamingInHeader: false,
  scenarioDetailsId: null,
  scenarioPermission: ScenariosPermission.OWNER,
  scenarioSettingsToLoadOnMap: getDefaultScenarioSettingsToLoadOnMap(),
  name: DEFAULT_SCENARIO_NAME,
  previousVersionDatetime: null,
  scenarioSelected: null,
  hasToShowAllScenarios: false,
};

export const initialState: ScenariosState = {
  selectedScenarioType: EScenarioType.EMPTY,
  economics: {
    ...scenarioSharedInitialState,
    dataForSaving: getScenarioSavedDataDefaultState<IEconomicsSavingData>(EScenarioType.ECONOMICS),
    lastSavedData: getScenarioSavedDataDefaultState<IEconomicsSavingData>(EScenarioType.ECONOMICS),
    scenarioDetailsToExecute: getScenarioSavedDataDefaultState<IEconomicsSavingData>(EScenarioType.ECONOMICS),
    scenarioDetailsPostExecution: getScenarioSavedDataDefaultState<IEconomicsSavingData>(EScenarioType.ECONOMICS),
  },
  plan: {
    ...scenarioSharedInitialState,
    dataForSaving: getScenarioSavedDataDefaultState<IPlanSavingData>(EScenarioType.PLAN),
    lastSavedData: getScenarioSavedDataDefaultState<IPlanSavingData>(EScenarioType.PLAN),
  },
};

export const {
  actions,
  reducer,
  name: sliceKey,
} = createSlice({
  name: 'scenarios',
  initialState,
  reducers: {
    loadScenarios(state, action: PayloadAction<LoadScenariosPayload>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].scenariosLoadingStatus = RequestStatus.LOADING;
      state[scenarioKey].scenarioListLoadingError = null;
    },
    changeScenarioType(state, action: PayloadAction<EScenarioType>) {
      state.selectedScenarioType = action.payload;
      Object.values(EScenarioType).forEach((type) => {
        const scenarioKey = getScenarioKey(type);
        state[scenarioKey].creatingError = null;
        state[scenarioKey].creatingStatus = RequestStatus.IDLE;
      });
    },
    scenariosLoaded(state, action: PayloadAction<{ scenarios: Scenario[] } & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].scenariosLoadingStatus = RequestStatus.SUCCESS;
      state[scenarioKey].scenarios = action.payload.scenarios;
    },
    resetScenarios(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      // not in use
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].scenarios = [];
    },
    scenariosError(state, action: PayloadAction<{ errorMessage: string } & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].scenariosLoadingStatus = RequestStatus.FAILURE;
      state[scenarioKey].scenarioListLoadingError = action.payload.errorMessage;
    },
    createScenario(state, action: PayloadAction<ScenarioCreateRequestPayload>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].creatingStatus = RequestStatus.LOADING;
      state[scenarioKey].creatingError = null;
    },
    createBulk(state, action: PayloadAction<BulkCreationPayload>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].creatingStatus = RequestStatus.LOADING;
      state[scenarioKey].creatingError = null;
    },
    bulkCreated(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].creatingStatus = RequestStatus.SUCCESS;
    },
    scenarioCreated(state, action: PayloadAction<ScenarioSavingResponse & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].creatingStatus = RequestStatus.SUCCESS;
      state[scenarioKey].scenarioDetailsId = action.payload.detailsId;
      state[scenarioKey].scenarioPermission = ScenariosPermission.OWNER;
      state[scenarioKey].scenarioSavingFormActive = false;
      state[scenarioKey].lastSavedData = cloneThroughJson(state[scenarioKey].dataForSaving);
      state[scenarioKey].name = action.payload.name;
      state[scenarioKey].scenarioSettingsToLoadOnMap = getUnloadableScenarioSettingsToLoadOnMap();
      state[scenarioKey].previousVersionDatetime = action.payload.lastUpdateDatetime;
    },
    createScenarioError(state, action: PayloadAction<{ errorMessage: string } & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].creatingStatus = RequestStatus.FAILURE;
      state[scenarioKey].creatingError = action.payload.errorMessage;
    },
    clearCreateScenarioError(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].creatingStatus = RequestStatus.IDLE;
      state[scenarioKey].creatingError = null;
    },
    updateScenario(state, action: PayloadAction<ScenarioUpdateRequestPayload>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].updatingStatus = RequestStatus.LOADING;
      state[scenarioKey].updatingError = null;
    },
    scenarioUpdated(state, action: PayloadAction<ScenarioSavingResponse & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].updatingStatus = RequestStatus.SUCCESS;
      state[scenarioKey].scenarioSavingFormActive = false;
      state[scenarioKey].lastSavedData = cloneThroughJson(state[scenarioKey].dataForSaving);
      state[scenarioKey].name = action.payload.name;
      state[scenarioKey].scenarioSettingsToLoadOnMap = getUnloadableScenarioSettingsToLoadOnMap();
      state[scenarioKey].previousVersionDatetime = action.payload.lastUpdateDatetime;
    },
    updateScenarioError(state, action: PayloadAction<{ errorMessage: string } & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].updatingStatus = RequestStatus.FAILURE;
      state[scenarioKey].updatingError = action.payload.errorMessage;
    },
    clearUpdateScenarioError(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].updatingStatus = RequestStatus.IDLE;
      state[scenarioKey].updatingError = null;
    },
    loadScenario(state, action: PayloadAction<ScenarioByIdRequestPayload>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].scenarioLoadingStatus = RequestStatus.LOADING;
      state[scenarioKey].scenarioLoadingError = null;
    },
    setScenario(
      state,
      {
        payload: { name, detailsId, details, permission, scenarioType },
      }: PayloadAction<{
        name: string;
        detailsId: number;
        permission: ScenariosPermission;
        details: ScenarioDetails;
        scenarioType: EScenarioType;
      }>,
    ) {
      const scenarioKey = getScenarioKey(scenarioType);
      const savedData =
        scenarioType === EScenarioType.ECONOMICS
          ? ({
              economicsParameters: {
                ...details.economicsParameters,
                economicVariables: {
                  ...details.economicsParameters.economicVariables,
                  current: {
                    ...details.economicsParameters.economicVariables.current,
                    simulateCashflowCutoff:
                      details.economicsParameters.economicVariables.current.simulateCashflowCutoff ?? true,
                    manualGas: details.economicsParameters.economicVariables.current.manualGas ?? null,
                    manualOil: details.economicsParameters.economicVariables.current.manualOil ?? null,
                  },
                },
              },
            } as unknown as IEconomicsSavingData)
          : ({
              wells: cloneThroughJson(details.wells.items),
              mapSettings: cloneThroughJson(details.mapSettings),
              prodCurvesSettings: cloneThroughJson(details.prodCurvesSettings),
              impactCurvesSettings: cloneThroughJson(details.impactCurvesSettings),
              wellParameters: cloneThroughJson(
                details.wells?.wellParameters ?? state.plan.lastSavedData.wellParameters,
              ),
            } as unknown as IPlanSavingData);

      state[scenarioKey].name = name;
      state[scenarioKey].scenarioDetailsId = detailsId;
      state[scenarioKey].scenarioPermission = permission;
      state[scenarioKey].dataForSaving = savedData;
      state[scenarioKey].lastSavedData = savedData;
      if (scenarioType === EScenarioType.ECONOMICS) {
        state[EScenarioType.ECONOMICS].scenarioDetailsToExecute = savedData;
        state[EScenarioType.ECONOMICS].scenarioDetailsPostExecution = savedData;
      }
      state[scenarioKey].previousVersionDatetime = details.dateUpdated;
      state[scenarioKey].scenarioSettingsToLoadOnMap = buildMapWellsSettings(details.wells.items);
    },
    resetScenario(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].name = DEFAULT_SCENARIO_NAME;
      state[scenarioKey].scenarioDetailsId = null;
      state[scenarioKey].scenarioPermission = ScenariosPermission.OWNER;
      state[scenarioKey].dataForSaving = getScenarioSavedDataDefaultState(action.payload.scenarioType);
      state[scenarioKey].lastSavedData = getScenarioSavedDataDefaultState(action.payload.scenarioType);
      if (scenarioKey === EScenarioType.ECONOMICS) {
        state[EScenarioType.ECONOMICS].scenarioDetailsToExecute = getScenarioSavedDataDefaultState(
          action.payload.scenarioType,
        );
        state[EScenarioType.ECONOMICS].scenarioDetailsPostExecution = getScenarioSavedDataDefaultState(
          action.payload.scenarioType,
        );
      }
      state[scenarioKey].previousVersionDatetime = null;
      state[scenarioKey].scenarioSettingsToLoadOnMap = getDefaultScenarioSettingsToLoadOnMap();
    },
    scenarioLoaded(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].scenarioLoadingStatus = RequestStatus.SUCCESS;
    },
    loadScenarioError(state, action: PayloadAction<{ errorMessage: string } & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].scenarioLoadingStatus = RequestStatus.FAILURE;
      state[scenarioKey].scenarioLoadingError = action.payload.errorMessage;
    },
    clearLoadScenarioError(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].scenarioLoadingStatus = RequestStatus.IDLE;
      state[scenarioKey].scenarioLoadingError = null;
    },
    startNewEmptyScenarioSaving(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].scenarioSavingFormActive = true;
      resetScenarioStateToInitPoint(state, action.payload.scenarioType);
    },
    startNewEmptyScenario(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      resetScenarioStateToInitPoint(state, action.payload.scenarioType);
    },
    setIsScenarioSaving(
      state,
      action: PayloadAction<{ scenarioSavingFormActive: boolean } & IScenarioTypeScenariosPage>,
    ) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].scenarioSavingFormActive = action.payload.scenarioSavingFormActive;
    },
    setFirstRunSelectedScenario(
      state,
      action: PayloadAction<{ firstRunSelectedScenario: boolean } & IScenarioTypeScenariosPage>,
    ) {
      state[getScenarioKey(action.payload.scenarioType)].firstRunSelectedScenario =
        action.payload.firstRunSelectedScenario;
    },
    setDataForSaving(state, action: PayloadAction<IEconomicsSavingData | IPlanSavingData>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      if (action.payload.scenarioType === EScenarioType.PLAN) {
        const { wells, mapSettings, prodCurvesSettings, impactCurvesSettings, wellParameters } =
          action.payload as IPlanSavingData;
        if (wells && mapSettings && prodCurvesSettings && impactCurvesSettings)
          state[scenarioKey].dataForSaving = {
            wells,
            mapSettings,
            prodCurvesSettings,
            impactCurvesSettings,
            wellParameters,
          } as unknown as IPlanSavingData;
      }
      if (action.payload.scenarioType === EScenarioType.ECONOMICS) {
        const { economicsParameters } = action.payload as IEconomicsSavingData;
        // if (economicsParameters)
        const data = {
          economicsParameters: {
            economicVariables: {
              ...(state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.economicVariables,
              ...economicsParameters?.economicVariables,
              current: {
                ...((state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.economicVariables
                  .current as any),
                ...(economicsParameters?.economicVariables.current ?? []),
                manualGas:
                  economicsParameters?.economicVariables.current?.manualGas ??
                  (state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.economicVariables
                    .current?.manualGas ??
                  null,
                manualOil:
                  economicsParameters?.economicVariables.current?.manualOil ??
                  (state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.economicVariables
                    .current?.manualOil ??
                  null,
                simulateCashflowCutoff:
                  economicsParameters?.economicVariables.current?.simulateCashflowCutoff ??
                  (state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.economicVariables
                    .current?.simulateCashflowCutoff ??
                  true,
              },
            },
            wellDesignCapex: {
              ...(state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.wellDesignCapex,
              ...(economicsParameters?.wellDesignCapex ?? ([] as any)),
            },
            wellIdentification: {
              ...(state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.wellIdentification,
              ...(economicsParameters?.wellIdentification ?? ([] as any)),
            },
          },
        } as unknown as IEconomicsSavingData;

        state[scenarioKey].dataForSaving = data;
        state[EScenarioType.ECONOMICS].scenarioDetailsToExecute = data;
      }
    },
    setWellParametersForPlanAfterLoadInitOptions(state, { payload }) {
      state[EScenarioType.PLAN].lastSavedData.wellParameters = payload;
    },
    setLastSavedData(state, action: PayloadAction<IEconomicsSavingData | IPlanSavingData>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      if (action.payload.scenarioType === EScenarioType.PLAN) {
        const { wells, mapSettings, prodCurvesSettings, impactCurvesSettings, wellParameters } =
          action.payload as IPlanSavingData;
        if (wells && mapSettings && prodCurvesSettings && impactCurvesSettings)
          state[scenarioKey].lastSavedData = {
            wells,
            mapSettings,
            prodCurvesSettings,
            impactCurvesSettings,
            wellParameters,
          } as unknown as IPlanSavingData;
      }
      if (action.payload.scenarioType === EScenarioType.ECONOMICS) {
        const { economicsParameters } = action.payload as IEconomicsSavingData;
        // // if (economicsParameters)
        state[scenarioKey].lastSavedData = {
          economicsParameters: {
            economicVariables: {
              ...(state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.economicVariables,
              ...(economicsParameters?.economicVariables ?? []),
              current: {
                ...((state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.economicVariables
                  .current as any),
                ...(economicsParameters?.economicVariables.current ?? []),
                manualGas:
                  economicsParameters?.economicVariables.current?.manualGas ??
                  (state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.economicVariables
                    .current?.manualGas ??
                  null,
                manualOil:
                  economicsParameters?.economicVariables.current?.manualOil ??
                  (state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.economicVariables
                    .current?.manualOil ??
                  null,
                simulateCashflowCutoff:
                  economicsParameters?.economicVariables.current?.simulateCashflowCutoff ??
                  (state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.economicVariables
                    .current?.simulateCashflowCutoff ??
                  true,
              },
            },
            wellDesignCapex: {
              ...(state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.wellDesignCapex,
              ...(economicsParameters?.wellDesignCapex ?? ([] as any)),
            },
            wellIdentification: {
              ...(state[scenarioKey].dataForSaving as IEconomicsSavingData).economicsParameters?.wellIdentification,
              ...(economicsParameters?.wellIdentification ?? ([] as any)),
            },
          },
        } as unknown as IEconomicsSavingData;
      }
    },
    setScenarioName(state, { payload }: PayloadAction<{ name: string } & IScenarioTypeScenariosPage>) {
      state[getScenarioKey(payload.scenarioType)].name = payload.name;
    },
    markCurrentDataForSavingAsSaved(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].lastSavedData = cloneThroughJson(state[scenarioKey].dataForSaving);
    },
    copyScenario(state, action: PayloadAction<ScenarioByIdRequestPayload & { shouldRefethScenarios: boolean }>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].copyingStatus = RequestStatus.LOADING;
      state[scenarioKey].copyingError = null;
    },
    scenarioCopied(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].copyingStatus = RequestStatus.SUCCESS;
    },
    scenarioCopyIdle(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].copyingStatus = RequestStatus.IDLE;
    },
    copyScenarioError(state, action: PayloadAction<{ errorMessage: string } & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].copyingStatus = RequestStatus.FAILURE;
      state[scenarioKey].copyingError = action.payload.errorMessage;
    },
    copiedScenarioId(state, action: PayloadAction<{ copiedScenarioId: number | null } & IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].copiedScenarioId = action.payload.copiedScenarioId;
    },
    leaveScenario(state, action: PayloadAction<ScenarioByIdRequestPayload>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].leavingStatus = RequestStatus.LOADING;
      state[scenarioKey].leavingError = null;
    },
    scenarioLeaved(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].leavingStatus = RequestStatus.SUCCESS;
    },
    scenarioLeaveIdle(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].leavingStatus = RequestStatus.IDLE;
    },
    leaveScenarioError(state, action: PayloadAction<{ errorMessage: string } & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].leavingStatus = RequestStatus.FAILURE;
      state[scenarioKey].copyingError = action.payload.errorMessage;
    },
    clearCopyScenarioError(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].copyingError = RequestStatus.IDLE;
      state[scenarioKey].copyingError = null;
    },
    deleteScenario(state, action: PayloadAction<ScenarioByIdRequestPayload>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].deletingStatus = RequestStatus.LOADING;
      state[scenarioKey].deletingError = null;
    },
    scenarioDeleted(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].deletingStatus = RequestStatus.SUCCESS;
    },
    scenarioDeleteIdle(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].deletingStatus = RequestStatus.IDLE;
    },
    deleteScenarioError(state, action: PayloadAction<{ deletingError: string } & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].deletingStatus = RequestStatus.FAILURE;
      state[scenarioKey].deletingError = action.payload.deletingError;
    },
    shareScenario(state, action: PayloadAction<ScenarioSharingRequestPayload>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].sharingStatus = RequestStatus.LOADING;
      state[scenarioKey].sharingError = null;
    },
    scenarioShared(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].sharingStatus = RequestStatus.SUCCESS;
    },
    shareScenarioError(
      state,
      action: PayloadAction<{ error: string | ScenarioSharingObjectError } & IScenarioTypeScenariosPage>,
    ) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].sharingStatus = RequestStatus.FAILURE;
      state[scenarioKey].sharingError = action.payload.error;
    },
    clearShareError(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].sharingStatus = RequestStatus.IDLE;
      state[scenarioKey].sharingError = null;
    },
    renameScenario(state, action: PayloadAction<ScenarioRenameRequestPayload>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].renamingStatus = RequestStatus.LOADING;
      state[scenarioKey].renamingError = null;
    },
    scenarioRenamed(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].renamingStatus = RequestStatus.SUCCESS;
      state[scenarioKey].scenarioRenamingInPanel = false;
      state[scenarioKey].scenarioRenamingInHeader = false;
    },
    renameScenarioError(state, action: PayloadAction<{ renamingStatus: string } & IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].renamingStatus = RequestStatus.FAILURE;
      state[scenarioKey].renamingError = action.payload.renamingStatus;
    },
    clearRenameScenarioError(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      state[scenarioKey].renamingStatus = RequestStatus.IDLE;
      state[scenarioKey].renamingError = null;
    },
    setIsScenarioRenamingInPanel(
      state,
      action: PayloadAction<{ scenarioRenamingInPanel: boolean } & IScenarioTypeScenariosPage>,
    ) {
      state[getScenarioKey(action.payload.scenarioType)].scenarioRenamingInPanel =
        action.payload.scenarioRenamingInPanel;
    },
    setIsScenarioRenamingInHeader(
      state,
      action: PayloadAction<{ scenarioRenamingInHeader: boolean } & IScenarioTypeScenariosPage>,
    ) {
      state[getScenarioKey(action.payload.scenarioType)].scenarioRenamingInHeader =
        action.payload.scenarioRenamingInHeader;
    },
    resetLoadedScenarioData(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      const scenarioKey = getScenarioKey(action.payload.scenarioType);
      if (scenarioKey === EScenarioType.PLAN) {
        state[scenarioKey].scenarioSettingsToLoadOnMap = getUnloadableScenarioSettingsToLoadOnMap();
      }
      if (scenarioKey === EScenarioType.ECONOMICS) {
        state[EScenarioType.ECONOMICS].scenarioDetailsToExecute = getScenarioSavedDataDefaultState(scenarioKey);
        state[EScenarioType.ECONOMICS].scenarioDetailsPostExecution = getScenarioSavedDataDefaultState(scenarioKey);
      }
      state[scenarioKey].dataForSaving = getScenarioSavedDataDefaultState(scenarioKey);
      state[scenarioKey].lastSavedData = getScenarioSavedDataDefaultState(scenarioKey);
    },
    scenarioSelected(state, action: PayloadAction<{ id: number } & IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].scenarioSelected = action.payload.id;
    },
    shownAllScenariosSelected(state, action: PayloadAction<IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].hasToShowAllScenarios = true;
    },
    allScenarioShown(state: ScenariosState, action: PayloadAction<IScenarioTypeScenariosPage>) {
      state[getScenarioKey(action.payload.scenarioType)].hasToShowAllScenarios = false;
    },
  },
});

export const createBulkType = actions.createBulk.type;
